import { useEffect } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { use_geolocation } from '@/lib/use-geolocation';
import { ErrorState } from '@/components/ui/error-state';
import { EmptyState } from '@/components/ui/empty-state';
import { use_tab } from './hooks';
import { SectionRenderer } from './section-renderer';
import type { TabName } from './types';

/**
 * Renders a whole app tab from the single aggregated endpoint.
 *
 * Reference behaviour worth copying on mobile:
 *  - request device location once, then refetch with coords (the first paint uses whatever
 *    the backend can do without them);
 *  - drive the UI off `location_source`, prompting for location only when it's NONE;
 *  - render sections in the order received and let SectionRenderer skip unknown types.
 */
export function TabView({ tab, title }: { tab: TabName; title: string }) {
  const geo = use_geolocation();

  // Ask for location on mount; harmless if denied — the tab still renders.
  useEffect(() => {
    geo.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const coords =
    geo.status === 'granted' && geo.lat != null && geo.lng != null
      ? { lat: geo.lat, lng: geo.lng }
      : undefined;

  const { data, isLoading, error } = use_tab(tab, coords);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-24" data-testid={`tab-${tab}`}>
      <div className="flex items-center justify-between px-1">
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">{title}</h1>
        {data && data.location_source !== 'DEVICE' && (
          <button
            type="button"
            onClick={() => geo.request()}
            className="flex items-center gap-1.5 rounded-full border-2 border-brand-outline-variant/25 bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            data-testid="tab-enable-location"
          >
            <MapPin className="h-3.5 w-3.5" />
            {geo.status === 'denied' ? 'Location off' : 'Use my location'}
          </button>
        )}
      </div>

      {error ? (
        <ErrorState description={error instanceof Error ? error.message : 'Could not load this tab.'} />
      ) : isLoading && !data ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : !data || data.sections.length === 0 ? (
        <EmptyState title="Nothing here yet" description="Check back soon." />
      ) : (
        data.sections.map((section, i) => <SectionRenderer key={`${section.type}-${i}`} section={section} />)
      )}
    </div>
  );
}
